import React, { useState, useEffect } from 'react';

import type { WebappResponse } from '@eventbrite/event-listing-service-client';

import { useAppContext } from '@shared/context/AppContext';
import { useListingsDesktopTwoRails } from '@/components/_shared/contexts/ExperimentsContext';
import { useShowFollowVenueCta } from '@/bsp/config';
import type { BasicInfo } from '@/types';

import { EventTitle } from './EventTitle';
import { OrganizerInfo } from './OrganizerInfo';
import { EventDateTime } from './EventDateTime/EventDateTime';
import { EventVenue } from './EventVenue/EventVenue';
import { CreatorCollections } from './CreatorCollections';
import { PresenterInfo } from './PresenterInfo';

import styles from './EventDetails.module.scss';

interface EventDetailsProps {
  basicInfo: BasicInfo;
  collections: WebappResponse['collections'];
  presenterInfo: WebappResponse['presenterInfo'];
}

export const EventDetails: React.FC<EventDetailsProps> = ({ basicInfo, collections, presenterInfo }) => {
  const { data } = useAppContext();
  const showFollowVenueCtaFlag = useShowFollowVenueCta();

  // Only show the Follow Venue CTA when the organizer is not the venue itself,
  // identified by matching names (normalized: trimmed, case-insensitive).
  const organizerName = basicInfo.organizer?.name?.trim().toLowerCase();
  const venueName = basicInfo.venue?.name?.trim().toLowerCase();
  const organizerIsVenue = !!organizerName && organizerName === venueName;
  const showFollowVenueCta = showFollowVenueCtaFlag && !organizerIsVenue;

  const shouldShowDateTime = !!basicInfo.startDate?.utc;

  const eventDateTimeComponent = shouldShowDateTime ? (
    <EventDateTime
      startDate={basicInfo.startDate}
      endDate={basicInfo.endDate ?? { utc: null, timezone: undefined }}
      locale={data.locale}
      isOnlineEvent={basicInfo.isOnline || false}
      isRecurringEvent={basicInfo.isSeries || false}
      hideStartDate={basicInfo.hideStartDate}
      hideEndDate={basicInfo.hideEndDate}
    />
  ) : null;

  const isListingsDesktopTwoRails = useListingsDesktopTwoRails();

  const [clientExperimentState, setClientExperimentState] = useState(false);

  useEffect(() => {
    setClientExperimentState(isListingsDesktopTwoRails);
  }, [isListingsDesktopTwoRails]);

  const experimentStatus = clientExperimentState ? 'active' : 'inactive';

  const venueAndDateTimeComponent = (
    <div className={styles.venueAndDateTimeWrapper} data-two-rails-experiment={experimentStatus}>
      <EventVenue venue={basicInfo.venue} isOnline={basicInfo.isOnline} showFollowVenueCta={showFollowVenueCta} />
      {eventDateTimeComponent}
    </div>
  );

  return (
    <div className={styles.wrapper} data-testid="event-details">
      <EventTitle title={basicInfo.name} />
      <div className={styles.organizerInfoWrapper}>
        <OrganizerInfo organizer={basicInfo.organizer} />
      </div>
      <CreatorCollections collections={collections} />
      {venueAndDateTimeComponent}
      {presenterInfo?.presenter && (
        <div className={styles.presenterInfoWrapper}>
          <PresenterInfo presenterInfo={presenterInfo} />
        </div>
      )}
    </div>
  );
};
